// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color } from "@eisinc/XDL/Utilities";
import { IconMemo } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import styled, { css } from "styled-components";

/**
 *
 * XNG-Changes:
 * style changes
 */
const toggleSizes = {
    default: {
        iconSize: 22,
        circleSize: 12,
        height: 16,
        width: 32,
        uncheckedOffset: -6,
        checkedOffset: 9,
    },
    small: {
        iconSize: 22,
        circleSize: 12,
        height: 16,
        width: 32,
        uncheckedOffset: -6,
        checkedOffset: 9,
    },
};

const Slider = styled.span<{ _sizeVariant: keyof typeof toggleSizes }>`
    margin: 0 8px;
    width: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].width}px;
    position: absolute;
    left: 0;
    top: 50%;
    transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
    display: flex;
    align-items: center;
`;

type SliderFrameProps = {
    _sizeVariant: keyof typeof toggleSizes;
    _color?: string;
};

// <XNG-Changes>
const clorizeIcon = (path: string, opacity = 1) => css`
    color: ${color(path, opacity)};
    fill: ${color(path, opacity)};
`;

const UnCheckedActiveSelector = `&[aria-checked="false"][aria-disabled="false"]`;
const UnCheckedDisabledSelector = `&[aria-checked="false"][aria-disabled="true"]`;
const CheckedActiveSelector = `&[aria-checked="true"][aria-disabled="false"]`;
const CheckedDisabledSelector = `&[aria-checked="true"][aria-disabled="true"]`;

const unCheckedStateStyles = css`
    ${UnCheckedActiveSelector} {
        background: ${color("common.ui2")};
        border: 1px solid ${color("common.ui2")};
        ${clorizeIcon("common.ui6")};
        &:hover {
            ${clorizeIcon("common.ui9")};
        }
    }

    ${UnCheckedDisabledSelector} {
        background: ${color("common.ui2", 0.33)};
        border: 1px solid ${color("common.ui2", 0.33)};
        ${clorizeIcon("text.main", 0.33)};
    }
`;

const checkedStateStyles = css`
    ${CheckedActiveSelector} {
        background: ${color("common.ui2")};
        border: 1px solid ${color("common.ui2")};
        ${clorizeIcon("common.ui6")};
        &:hover {
            ${clorizeIcon("common.ui9")};
        }
    }
    ${CheckedDisabledSelector} {
        background: ${color("common.ui2", 0.33)};
        border: 1px solid ${color("common.ui2", 0.33)};
        ${clorizeIcon("text.main", 0.33)};
    }
`;
// </XNG-Changes>

const SliderFrame = styled.div<SliderFrameProps>`
    min-width: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].width}px;
    height: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].height}px;
    border-radius: 15px;
    overflow: hidden;
    position: relative;
    display: inline-block;
    box-sizing: border-box;
    cursor: pointer;

    ${unCheckedStateStyles}
    ${checkedStateStyles}

    &[aria-checked="false"] ${Slider} {
        transform: translate(${({ _sizeVariant }) => toggleSizes[_sizeVariant].uncheckedOffset}px, -50%);
    }

    &[aria-checked="true"] ${Slider} {
        transform: translate(${({ _sizeVariant }) => toggleSizes[_sizeVariant].checkedOffset}px, -50%);
    }

    &[aria-checked="true"]:focus {
        outline: none;
    }

    &[aria-disabled="true"] {
        cursor: not-allowed;
    }

    & + label {
        margin-left: 8px;
    }
    &:focus {
        outline: none;
    }
    ${injectCss}
`;

type Props = SliderFrameProps & React.InputHTMLAttributes<HTMLInputElement>;

/**
 *
 * XNG-Changes:
 * remove Check and  X icons
 */
const ToggleInput: React.FC<Props> = ({ _sizeVariant = "default", ...otherProps }) => (
    <SliderFrame _sizeVariant={_sizeVariant} {...otherProps}>
        <Slider _sizeVariant={_sizeVariant}>
            {/* <IconMemo
                src="Check"
                size={toggleSizes[_sizeVariant].iconSize}
                color="currentColor"
                css={css`
                    margin-left: -1px;
                `}
            /> */}
            <IconMemo
                src="FillCircle"
                size={toggleSizes[_sizeVariant].circleSize}
                color="currentColor"
                css={css`
                    margin: 0;
                    transition: all 0s;
                `}
            />
            {/* <IconMemo
                src="X"
                size={toggleSizes[_sizeVariant].iconSize}
                color="currentColor"
                css={css`
                    margin-left: 2px;
                `}
            /> */}
        </Slider>
    </SliderFrame>
);

export default ToggleInput;
